import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import io, { Socket } from 'socket.io-client';
import { graphics } from './ChessLib';
import { ChessPiece } from './types';
import './ChessReplay.css';
import logger from '../../utils/logger';

interface Player {
    id: string;
    color: string;
    userId: string | null;
    disconnected?: boolean;
}

interface GameInfo {
    id: string;
    name: string;
    state: string;
    winnerId: string | null;
    endReason: string;
    completedAt: string;
    players: Player[];
    currentPlayer?: string | null;
}

interface Move {
    moveNumber: number;
    playerId: string;
    from: string;
    to: string;
    piece: string;
    capturedPiece: string | null;
    isCheck: boolean;
    isCheckmate: boolean;
    timeLeft: number | null;
    createdAt: string;
}

interface ReplayData {
    game: GameInfo;
    moves: Move[];
}

type Board = (ChessPiece)[][];

const INITIAL_BOARD: Board = [
    ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'],
    ['P', 'P', 'P', 'P', 'P', 'P', 'P', 'P'],
    [null, null, null, null, null, null, null, null],
    [null, null, null, null, null, null, null, null],
    [null, null, null, null, null, null, null, null],
    [null, null, null, null, null, null, null, null],
    ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p'],
    ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r']
];

function ChessReplay() {
    const { gameId } = useParams<{ gameId: string }>();
    const [replayData, setReplayData] = useState<ReplayData | null>(null);
    const [currentMoveIndex, setCurrentMoveIndex] = useState<number>(-1);
    const [board, setBoard] = useState<Board>(INITIAL_BOARD);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isLive, setIsLive] = useState<boolean>(false);
    const [liveGame, setLiveGame] = useState<any>(null);
    const socketRef = useRef<Socket | null>(null);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const navigate = useNavigate();

    useEffect(() => {
        if (gameId) {
            fetchReplayData(gameId);
        }

        // Cleanup socket on unmount
        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [gameId]);

    useEffect(() => {
        if (replayData) {
            updateBoardToMove(currentMoveIndex);
        }
    }, [currentMoveIndex, replayData]);

    const fetchReplayData = async (id: string): Promise<void> => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${SERVER_URL}/api/chess/game/${id}/replay`);

            if (!response.ok) {
                throw new Error('Failed to fetch replay data');
            }

            const data: ReplayData = await response.json();
            logger.debug("Fetched replay data", { gameId: id, moves: data.moves.length, state: data.game.state });

            setReplayData(data);

            // Check if game is live (ongoing or waiting)
            const isGameLive = data.game.state === 'ongoing' || data.game.state === 'waiting';
            setIsLive(isGameLive);

            if (isGameLive) {
                // Connect to socket for live updates
                connectToLiveGame(id);
            }
        } catch (err) {
            logger.error('Error fetching replay data:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const connectToLiveGame = (id: string): void => {
        logger.info("Connecting to live game", { gameId: id });

        const socket = io(`${SERVER_URL}/chess`, {
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
            reconnectionAttempts: 10,
        });

        socketRef.current = socket;

        // Join game room to receive updates
        socket.emit("viewGame", id);

        // Listen for game updates
        socket.on("gameUpdated", (updatedGame: any) => {
            logger.debug("Live game updated", { gameId: id, state: updatedGame.state });
            setLiveGame(updatedGame);

            // Refetch moves when game updates
            fetch(`${SERVER_URL}/api/chess/game/${id}/replay`)
                .then(response => response.json())
                .then((data: ReplayData) => {
                    setReplayData({
                        ...data,
                        game: {
                            ...data.game,
                            state: updatedGame.state,
                            currentPlayer: updatedGame.currentPlayer,
                            players: updatedGame.players.map((p: any) => ({
                                id: p.id,
                                color: p.color,
                                userId: p.userId || null,
                                disconnected: p.disconnected || false,
                            })),
                        },
                    });
                })
                .catch(err => logger.error('Error refetching moves:', err));

            // If game is no longer live, disconnect socket
            if (updatedGame.state !== 'ongoing' && updatedGame.state !== 'waiting') {
                setIsLive(false);
            }
        });
    };

    const parseSquare = (square: string): { row: number, col: number } => {
        const col = square.charCodeAt(0) - 97; // 'a' = 0, 'b' = 1, etc.
        const row = 8 - parseInt(square[1]); // '8' = 0, '7' = 1, etc.
        return { row, col };
    };

    const updateBoardToMove = (moveIndex: number): void => {
        if (!replayData) return;

        // Start with initial board
        const newBoard: Board = INITIAL_BOARD.map(row => [...row]);

        // Apply all moves up to moveIndex
        for (let i = 0; i <= moveIndex && i < replayData.moves.length; i++) {
            const move = replayData.moves[i];
            const from = parseSquare(move.from);
            const to = parseSquare(move.to);

            // Move piece
            const piece = newBoard[from.row][from.col];
            newBoard[to.row][to.col] = piece;
            newBoard[from.row][from.col] = null;
        }

        setBoard(newBoard);
    };

    const handleFirst = (): void => {
        setCurrentMoveIndex(-1);
    };

    const handlePrevious = (): void => {
        if (currentMoveIndex > -1) {
            setCurrentMoveIndex(prev => prev - 1);
        }
    };

    const handleNext = (): void => {
        if (replayData && currentMoveIndex < replayData.moves.length - 1) {
            setCurrentMoveIndex(prev => prev + 1);
        }
    };

    const handleLast = (): void => {
        if (replayData) {
            setCurrentMoveIndex(replayData.moves.length - 1);
        }
    };

    const navigateToGames = (): void => {
        navigate("/mferchess/games");
    };

    const navigateToLobby = (): void => {
        navigate("/mferchess/");
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getWinnerColor = (): string | null => {
        if (!replayData || !replayData.game.winnerId) return null;
        const winner = replayData.game.players.find(p => p.id === replayData.game.winnerId);
        return winner ? winner.color : null;
    };

    const getGameResultText = (): string => {
        if (!replayData) return '';

        if (!replayData.game.winnerId) {
            return 'Draw';
        }

        const winner = replayData.game.players.find(p => p.id === replayData.game.winnerId);
        const loser = replayData.game.players.find(p => p.id !== replayData.game.winnerId);

        if (!winner) return 'Draw';

        const winnerColor = winner.color === 'plain' ? 'plain' : 'charcoal';
        const loserColor = loser?.color === 'plain' ? 'plain' : 'charcoal';

        // Build winner and loser identity strings
        const winnerIdentity = winner.userId
            ? `${winner.userId} (${winnerColor})`
            : winnerColor;
        const loserIdentity = loser?.userId
            ? `${loser.userId} (${loserColor})`
            : loserColor;

        return `${winnerIdentity} defeats ${loserIdentity}`;
    };

    const getCurrentMove = (): Move | null => {
        if (!replayData || currentMoveIndex < 0 || currentMoveIndex >= replayData.moves.length) {
            return null;
        }
        return replayData.moves[currentMoveIndex];
    };

    const formatMove = (move: Move): string => {
        const piece = move.piece.toUpperCase();
        const capture = move.capturedPiece ? 'x' : '';
        const check = move.isCheck ? '+' : '';
        const checkmate = move.isCheckmate ? '#' : '';
        return `${piece}${capture}${move.to}${check}${checkmate}`;
    };

    if (loading) {
        return (
            <div className="replay-container">
                <div className="loading">Loading replay...</div>
            </div>
        );
    }

    if (error || !replayData) {
        return (
            <div className="replay-container">
                <div className="error">
                    <p>Error: {error || 'Failed to load replay'}</p>
                    <button onClick={navigateToGames}>Back to Games</button>
                </div>
            </div>
        );
    }

    const currentMove = getCurrentMove();

    return (
        <div className="replay-container">
            {/* Title */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '48px', margin: '0', color: 'black' }}>
                    Chess {isLive ? 'Live View' : 'Replay'}: {replayData.game.name}
                    {isLive && (
                        <span className="live-indicator">
                            <span className="live-dot"></span>
                            LIVE
                        </span>
                    )}
                </h1>
                {!isLive && (
                    <div style={{ fontSize: '24px', marginTop: '10px', color: 'black' }}>
                        <strong>Result:</strong> {getGameResultText()}
                    </div>
                )}
            </div>

            {/* Replay Controls - Above Board */}
            <div className="replay-controls">
                <button
                    className="replay-control-btn"
                    onClick={handleFirst}
                    disabled={currentMoveIndex <= -1}
                >
                    ⏮<span className="btn-text"> First</span>
                </button>
                <button
                    className="replay-control-btn"
                    onClick={handlePrevious}
                    disabled={currentMoveIndex <= -1}
                >
                    ◀<span className="btn-text"> Previous</span>
                </button>
                <span className="move-counter">
                    <span className="move-label">Move: </span>{currentMoveIndex + 1} / {replayData.moves.length}
                </span>
                <button
                    className="replay-control-btn"
                    onClick={handleNext}
                    disabled={currentMoveIndex >= replayData.moves.length - 1}
                >
                    <span className="btn-text">Next </span>▶
                </button>
                <button
                    className="replay-control-btn"
                    onClick={handleLast}
                    disabled={currentMoveIndex >= replayData.moves.length - 1}
                >
                    <span className="btn-text">Last </span>⏭
                </button>
            </div>

            {/* Board - Centered */}
            <div className="chess-replay-board-section">
                <div className="chess-replay-board-wrapper">
                    <div className="chess-container">
                        <div className="row-labels">
                            {['8', '7', '6', '5', '4', '3', '2', '1'].map(label => (
                                <div key={label} className="row-label">{label}</div>
                            ))}
                        </div>
                        <div className="chessboard-wrapper">
                            <div className="column-labels">
                                {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(label => (
                                    <div key={label} className="column-label">{label}</div>
                                ))}
                            </div>
                            <div className="chessboard">
                                {board.map((row, rowIndex) => (
                                    row.map((cell, cellIndex) => {
                                        const isDarkSquare = (rowIndex + cellIndex) % 2 !== 0;
                                        const squareName = `${String.fromCharCode(97 + cellIndex)}${8 - rowIndex}`;
                                        const isFromSquare = currentMove && currentMove.from === squareName;
                                        const isToSquare = currentMove && currentMove.to === squareName;

                                        const classNames = [
                                            'square',
                                            isDarkSquare ? 'black-square' : 'white-square',
                                            isFromSquare ? 'from-square' : '',
                                            isToSquare ? 'to-square' : ''
                                        ].filter(Boolean).join(' ');

                                        return (
                                            <div
                                                key={`${rowIndex}-${cellIndex}`}
                                                className={classNames}
                                            >
                                                {cell && (
                                                    <img
                                                        src={graphics[`${cell.toLowerCase()}-${cell === cell.toUpperCase() ? 'white' : 'black'}`]}
                                                        alt={cell}
                                                        className="piece-img"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Move History - Below Board */}
            <div className="chess-replay-moves-section">
                <div className="chess-replay-moves-wrapper">
                    <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '28px' }}>Move History</h2>
                    <div className="moves-table-wrapper" style={{
                        overflowY: 'auto',
                        border: '1px solid black',
                        borderRadius: '4px',
                        minHeight: '300px',
                        maxHeight: '500px'
                    }}>
                        <table className="moves-table" style={{
                            fontFamily: "'Sartoshi', sans-serif",
                            color: 'black',
                            borderCollapse: 'collapse',
                            width: '100%',
                            fontSize: '18px'
                        }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid black', fontWeight: 'bold', backgroundColor: 'black', color: 'white', position: 'sticky', top: 0 }}>#</th>
                                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid black', fontWeight: 'bold', backgroundColor: 'black', color: 'white', position: 'sticky', top: 0 }}>Piece</th>
                                    <th style={{ padding: '10px', textAlign: 'center', border: '1px solid black', fontWeight: 'bold', backgroundColor: 'black', color: 'white', position: 'sticky', top: 0 }}>Move</th>
                                </tr>
                            </thead>
                            <tbody>
                                {replayData.moves.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} style={{ textAlign: 'center', padding: '8px' }}>No moves yet</td>
                                    </tr>
                                ) : (
                                    replayData.moves.map((move, index) => {
                                        const player = replayData.game.players.find(p => p.id === move.playerId);
                                        const playerColor = player?.color || 'plain';
                                        const bgColor = playerColor === 'plain' ? '#ffffff' : '#a9a9a9';
                                        const isActive = index === currentMoveIndex;

                                        return (
                                            <tr
                                                key={index}
                                                onClick={() => setCurrentMoveIndex(index)}
                                                style={{
                                                    backgroundColor: isActive ? 'rgba(255, 255, 0, 0.5)' : bgColor,
                                                    color: '#000000',
                                                    cursor: 'pointer',
                                                    fontWeight: isActive ? 'bold' : 'normal'
                                                }}
                                            >
                                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid black', fontWeight: 'bold', color: '#666' }}>{index + 1}</td>
                                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid black' }}>
                                                    <img
                                                        src={graphics[`${move.piece.toLowerCase()}-${playerColor === 'plain' ? 'white' : 'black'}`]}
                                                        alt={move.piece}
                                                        style={{ width: '30px', height: '30px', verticalAlign: 'middle' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid black', fontFamily: "'Sartoshi', sans-serif", fontSize: '20px' }}>
                                                    <span>{move.from}-{move.to}</span>
                                                    {move.capturedPiece && (
                                                        <img
                                                            src={graphics[`${move.capturedPiece.toLowerCase()}-${playerColor === 'plain' ? 'black' : 'white'}`]}
                                                            alt={`captured ${move.capturedPiece}`}
                                                            style={{ width: '30px', height: '30px', verticalAlign: 'middle', marginLeft: '8px' }}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Navigation Buttons - Bottom */}
            <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <button onClick={navigateToLobby} className="back-button">
                    Active Games
                </button>
                <button onClick={navigateToGames} className="back-button">
                    Completed Games
                </button>
            </div>
        </div>
    );
}

export default ChessReplay;
